/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { format } from "date-fns";
import { useI18n } from "hooks/i18n/i18n";
import React from "react";
import { Link } from "react-router-dom";
import { changeStyleWithHosting } from "utility/hosting";

export const ResaleApplicationReception: React.FC<{
  resaleEntryURL: string;
  applicationStartAt: number;
  applicationEndAt: number;
  lotteryDate: number;
  resaleStatus: "accepting" | "beforeAcceptance" | "closed";
}> = ({
  resaleEntryURL,
  applicationStartAt,
  applicationEndAt,
  lotteryDate,
  resaleStatus,
}) => {
  const { t, i18n } = useI18n();

  const isAccepting = resaleStatus === "accepting";
  const statusLabel = (() => {
    switch (resaleStatus) {
      case "accepting":
        return t("event.resale.status.accepting");
      case "beforeAcceptance":
        return t("event.resale.status.listingAccepting");
      case "closed":
        return t("event.resale.status.closed");
    }
  })();

  const applicationStartAtLabel = format(
    new Date(applicationStartAt),
    "MM/dd HH:mm"
  );
  const applicationEndAtDate = new Date(applicationEndAt);
  const applicationEndAtWeekday = new Intl.DateTimeFormat(
    i18n.language.replace("_", "-"),
    { weekday: "short" }
  ).format(applicationEndAtDate);
  const applicationEndAtLabel = `${format(
    applicationEndAtDate,
    "MM/dd"
  )}（${applicationEndAtWeekday}）${format(applicationEndAtDate, "HH:mm")}`;
  const lotteryDateLabel = format(new Date(lotteryDate), "MM/dd HH:mm");

  const classes = styles({ isAccepting });

  return (
    <section css={classes.root} aria-label={t("event.resale.officialResale")}>
      <div css={classes.heading}>
        <h5>{t("event.resale.officialResale")}</h5>
        <p
          css={[
            classes.status,
            resaleStatus === "closed" && classes.closedStatus,
          ]}
        >
          {statusLabel}
        </p>
      </div>
      <dl css={classes.schedule}>
        <div>
          <dt>{t("event.resale.applicationDeadline")}</dt>
          <dd>{applicationEndAtLabel}</dd>
        </div>
        <div>
          <dt>{t("event.resale.lotteryResult")}</dt>
          <dd>{`${lotteryDateLabel} ${t("event.resale.after")}`}</dd>
        </div>
      </dl>
      {isAccepting ? (
        <Link to={resaleEntryURL} css={classes.action}>
          {t("event.resale.action.apply")}
        </Link>
      ) : (
        <p css={classes.action}>
          {resaleStatus === "beforeAcceptance"
            ? t("event.resale.action.beforeAcceptance", {
                applicationStartAt: applicationStartAtLabel,
              })
            : t("event.resale.action.closed")}
        </p>
      )}
    </section>
  );
};

type StyleProps = {
  isAccepting: boolean;
};

const styles = ({ isAccepting }: StyleProps) => {
  const { accentColor } = changeStyleWithHosting().baseStyles;

  return {
    root: css`
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 16px 4%;
      background-color: #fff;
      border-radius: 0 0 5px 5px;
      &::before {
        content: "";
        height: 2px;
        background-color: ${accentColor};
      }
      @media screen and (min-width: 768px) {
        padding: 16px;
      }
    `,
    heading: css`
      display: flex;
      justify-content: space-between;
      align-items: center;
      h5 {
        margin: 0;
        font-size: 16px;
        font-weight: bold;
      }
    `,
    status: css`
      color: ${accentColor};
      font-size: 12px;
      font-weight: bold;
    `,
    closedStatus: css`
      color: #c77d38;
    `,
    schedule: css`
      margin: 0;
      div {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 12px;
        & + div {
          margin-top: 6px;
        }
      }
      dt {
        color: #666;
        font-size: 12px;
      }
      dd {
        color: #333;
        font-size: 12px;
        font-weight: bold;
        text-align: right;
      }
    `,
    action: css`
      display: block;
      width: 100%;
      padding: 12px 12px;
      margin-top: 4px;
      color: #fff;
      font-size: 12px;
      font-weight: bold;
      text-align: center;
      background-color: ${isAccepting ? accentColor : "#d4d4d4"};
    `,
  };
};
